import React, { useState, useEffect, useRef } from 'react';
import { HashRouter, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import URLSearchParams from '@ungap/url-search-params';
import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/browser';

import { createApolloClient } from '@mdlinx/frontend-shared';
import { defaults } from '@mdlinx/shared/dist/defaults';

import { useMeQuery } from 'generated/client';
import introspectionResult from 'generated/possible-types';
import { ChangeEmail } from 'app/scenes/ChangeEmail';
import { EmailActionHandler } from 'app/scenes/EmailActionHandler/EmailActionHandler';
import { Login } from 'app/scenes/Login';
import { Logout } from 'app/scenes/Logout';
import { SignUp } from 'app/scenes/SignUp';
import { SetAcquisitionParams } from 'app/scenes/SetAcquisitionParams';
import { ProviderUserProfile } from 'app/scenes/SocialAccountForm';
import { trackEvent, getLocationDetails } from 'lib/analytics';
import { getRegistrationTrackingData } from 'lib/tracking';

Sentry.init({
  dsn: 'https://9d81bc1d3c9f4328837b0bc923eaf025@sentry.io/3580268',
  environment: window?.location.hostname === 'auth.g.c.mdlinx.com' ? 'production' : 'development',
});

const setSentryUserId = (() => {
  let scopeConfigured = false;

  return (userId: string | undefined): void => {
    if (!scopeConfigured && typeof userId === 'string') {
      Sentry.configureScope(scope => {
        scope.setUser({ id: userId });
      });
      scopeConfigured = true;
    }
  };
})();

const apiBaseUrl =
  process.env.NODE_ENV === 'development'
    ? `http://localhost.mdlinx.com:${defaults.api.auth.port}`
    : `https://auth-api.${process.env.REACT_APP_ROOT_DOMAIN_NAME}`;

export const apolloClient = createApolloClient({
  baseUrl: apiBaseUrl,
  possibleTypes: introspectionResult.possibleTypes,
  authorization: process.env.REACT_APP_ENVNAME === 'dev',
});

const App = () => (
  <ApolloProvider client={apolloClient}>
    <HashRouter>
      <Main />
    </HashRouter>
  </ApolloProvider>
);

type AppContextType = {
  isDismissable: boolean;
  iframeLocation: 'in-well' | 'modal';
};

const initialAppContext: AppContextType = {
  isDismissable: false,
  iframeLocation: 'modal',
};

export const AppContext = React.createContext<AppContextType>(initialAppContext);

const Main = () => {
  const history = useHistory();
  const meResponse = useMeQuery({ fetchPolicy: 'network-only' });
  const location = useLocation();
  const [initialProfileValues, setInitialProfileValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    postalCode: '',
    selfReportedUsmd: true,
  });

  const searchParams = location.search && new URLSearchParams(location.search);
  const [appContext] = useState({
    isDismissable: searchParams && searchParams.get('modalIsDismissable') === 'true' ? true : false,
    iframeLocation:
      searchParams && searchParams.get('iframeLocation') === 'in-well' ? 'in-well' : initialAppContext.iframeLocation,
  } as AppContextType);
  const registrationTrackingData = useRef(getRegistrationTrackingData(new URLSearchParams(location.search)));

  useEffect(() => {
    trackEvent(`auth_modal_opened`, { ...getLocationDetails(location.pathname, location.search) });
  }, []);

  useEffect(() => {
    trackEvent(`auth_page_transition`, { ...getLocationDetails(location.pathname, location.search) });
  }, [location]);

  setSentryUserId(meResponse.data?.me?.account?.uid);

  const onSocialSignUp = (providerUserProfile?: ProviderUserProfile) => {
    const me = !meResponse.loading && meResponse.data && meResponse.data.me;

    if (providerUserProfile) {
      setInitialProfileValues({
        ...providerUserProfile,
        postalCode: '',
        selfReportedUsmd: true,
      });
      return history.push('/sign-up/check-info');
    }

    setInitialProfileValues({
      firstName: '',
      lastName: '',
      email: (me && me.account && me.account.email) || '',
      postalCode: '',
      selfReportedUsmd: true,
    });

    history.push('/sign-up/check-info');
  };

  // update auth-fronted
  return (
    <AppContext.Provider value={appContext}>
      <Switch>
        <Route path="/logout" component={Logout} />
        <Route path="/set-acquisition-params" component={SetAcquisitionParams} />

        <Route path="/action" component={EmailActionHandler} />
        <Route path="/change-email" component={ChangeEmail} />

        <Route path="/sign-up">
          <SignUp
            registrationTrackingData={registrationTrackingData.current}
            onSocialSignUp={onSocialSignUp}
            initialProfileValues={initialProfileValues}
          />
        </Route>

        <Route path="/login">
          <Login registrationTrackingData={registrationTrackingData.current} onSocialSignUp={onSocialSignUp} />
        </Route>
      </Switch>
    </AppContext.Provider>
  );
};

export default App;
